// src/features/dashboard/components/Manager/ProductivityReport/ProductiveAppMixChart.tsx

import {
  useMemo,
  useState,
} from 'react';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import {
  ProductiveAppMixResponse,
} from '../../../types/Manager/productivityReport.types';

import {
  PRODUCTIVE_APP_COLORS,
  CHART_COLORS_500,
} from '../../../constants/chartColors';

/* ==========================================================================
 * PROPS
 * ========================================================================== */

interface Props {
  data: ProductiveAppMixResponse[];
}

/* ==========================================================================
 * TYPES
 * ========================================================================== */

interface TooltipPayloadItem {
  name?: string;
  value?: number | string;
  payload?: ChartDataItem;
}

interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
  originalIndex: number;
}

interface PaletteColor {
  code: string;
  hue: number;
  saturation: number;
  lightness: number;
}

/* ==========================================================================
 * BASIC HELPERS
 * ========================================================================== */

/**
 * Safely converts API percentage values to numbers.
 */
const getSafePercentage = (
  value: unknown,
): number => {
  const numberValue =
    Number(value);

  if (
    !Number.isFinite(
      numberValue,
    )
  ) {
    return 0;
  }

  return Math.max(
    0,
    numberValue,
  );
};

/**
 * Show labels only for meaningful
 * chart segments.
 *
 * Tiny slices remain visible but don't
 * create unreadable text.
 */
const shouldShowLabel = (
  value: number,
): boolean => {
  return value >= 3;
};

/* ==========================================================================
 * HEX → HSL
 * ========================================================================== */

const hexToHsl = (
  hex: string,
): {
  hue: number;
  saturation: number;
  lightness: number;
} | null => {
  const normalized =
    hex.replace('#', '');

  if (
    normalized.length !== 6
  ) {
    return null;
  }

  const red =
    Number.parseInt(
      normalized.substring(
        0,
        2,
      ),
      16,
    ) / 255;

  const green =
    Number.parseInt(
      normalized.substring(
        2,
        4,
      ),
      16,
    ) / 255;

  const blue =
    Number.parseInt(
      normalized.substring(
        4,
        6,
      ),
      16,
    ) / 255;

  if (
    !Number.isFinite(red) ||
    !Number.isFinite(green) ||
    !Number.isFinite(blue)
  ) {
    return null;
  }

  const max =
    Math.max(
      red,
      green,
      blue,
    );

  const min =
    Math.min(
      red,
      green,
      blue,
    );

  const difference =
    max - min;

  let hue = 0;

  if (difference !== 0) {
    if (max === red) {
      hue =
        60 *
        (((green - blue) /
          difference) %
          6);
    } else if (
      max === green
    ) {
      hue =
        60 *
        ((blue - red) /
          difference +
          2);
    } else {
      hue =
        60 *
        ((red - green) /
          difference +
          4);
    }
  }

  if (hue < 0) {
    hue += 360;
  }

  const lightness =
    (max + min) / 2;

  const saturation =
    difference === 0
      ? 0
      : difference /
        (1 -
          Math.abs(
            2 *
              lightness -
              1,
          ));

  return {
    hue,
    saturation:
      saturation * 100,
    lightness:
      lightness * 100,
  };
};

/* ==========================================================================
 * HUE DISTANCE
 * ========================================================================== */

const getHueDistance = (
  first: number,
  second: number,
): number => {
  const difference =
    Math.abs(
      first - second,
    );

  return Math.min(
    difference,
    360 - difference,
  );
};

/* ==========================================================================
 * MULTICOLOR PALETTE
 * ========================================================================== */

/**
 * Converts the centralized 500-color palette
 * into HSL information.
 *
 * No new colors are created here.
 */
const getMulticolorPalette =
  (): PaletteColor[] => {
    return CHART_COLORS_500
      .map((item) => {
        const hsl =
          hexToHsl(item.code);

        if (!hsl) {
          return null;
        }

        return {
          code: item.code,
          hue: hsl.hue,
          saturation:
            hsl.saturation,
          lightness:
            hsl.lightness,
        };
      })
      .filter(
        (
          item,
        ): item is PaletteColor =>
          item !== null,
      );
  };

/* ==========================================================================
 * ACCENT COLOR SELECTION
 * ========================================================================== */

/**
 * Select different accent colors for the
 * small slices.
 *
 * The blue palette is intentionally NOT used
 * here because the main slices already use it.
 *
 * This function finds colors from the centralized
 * 500-color palette that are sufficiently different
 * from each other.
 */
const getAccentColors = (
  count: number,
): string[] => {
  if (
    count <= 0
  ) {
    return [];
  }

  const palette =
    getMulticolorPalette();

  if (
    palette.length === 0
  ) {
    return [];
  }

  /**
   * Prefer medium/dark colors because
   * white text and small pie slices are
   * easier to distinguish.
   */
  const candidates =
    palette.filter(
      (item) =>
        item.saturation >= 45 &&
        item.lightness >= 35 &&
        item.lightness <= 68,
    );

  const usableCandidates =
    candidates.length >= count
      ? candidates
      : palette;

  const selected:
    PaletteColor[] = [];

  const selectedCodes =
    new Set<string>();

  /**
   * We intentionally use target hue positions
   * around the color wheel.
   *
   * This gives accent colors such as:
   *
   * green
   * teal
   * purple
   * orange
   * pink
   *
   * instead of several nearly identical colors.
   */
  const targetHues = [
    155,
    185,
    260,
    30,
    325,
    95,
    215,
    285,
    5,
    125,
  ];

  for (
    let index = 0;
    index < count;
    index += 1
  ) {
    const targetHue =
      targetHues[
        index %
          targetHues.length
      ];

    let best:
      PaletteColor | null =
      null;

    let bestScore =
      Number.NEGATIVE_INFINITY;

    for (
      const candidate of usableCandidates
    ) {
      if (
        selectedCodes.has(
          candidate.code.toLowerCase(),
        )
      ) {
        continue;
      }

      const hueDistance =
        getHueDistance(
          targetHue,
          candidate.hue,
        );

      const quality =
        100 -
        Math.abs(
          candidate.lightness -
            52,
        );

      const score =
        hueDistance * 5 +
        quality;

      if (
        score >
        bestScore
      ) {
        bestScore = score;
        best = candidate;
      }
    }

    if (best) {
      selected.push(best);

      selectedCodes.add(
        best.code.toLowerCase(),
      );
    }
  }

  return selected.map(
    (item) => item.code,
  );
};

/* ==========================================================================
 * MAIN COLOR ASSIGNMENT
 * ========================================================================== */

/**
 * Main color strategy:
 *
 * ┌──────────────────────────────────────────┐
 * │ Largest slices                           │
 * │                                          │
 * │ Navy Blue                                │
 * │ Royal Blue                               │
 * │ Sky Blue                                 │
 * │ Light Blue                               │
 * │                                          │
 * │        BLUE IS THE MAIN THEME            │
 * ├──────────────────────────────────────────┤
 * │ Smaller slices                           │
 * │                                          │
 * │ Teal / Green / Purple / Orange / Pink    │
 * └──────────────────────────────────────────┘
 *
 * This is intentionally based on VALUE,
 * not API order.
 */
const buildChartColors = (
  data: ProductiveAppMixResponse[],
): Map<number, string> => {
  const colorMap =
    new Map<number, string>();

  if (
    !Array.isArray(data) ||
    data.length === 0
  ) {
    return colorMap;
  }

  /* ----------------------------------------------------------
   * PREPARE VALUES
   * ---------------------------------------------------------- */

  const values = data.map(
    (
      item,
      index,
    ) => ({
      index,
      value:
        getSafePercentage(
          item.contributionPercent,
        ),
    }),
  );

  /**
   * Sort by percentage descending.
   *
   * This is ONLY for assigning colors.
   *
   * The actual chart order remains unchanged.
   */
  const ranked =
    [...values].sort(
      (a, b) =>
        b.value - a.value,
    );

  /* ----------------------------------------------------------
   * BLUE PALETTE
   * ---------------------------------------------------------- */

  const blueColors =
    PRODUCTIVE_APP_COLORS.map(
      (item) => item.code,
    );

  /**
   * We reserve the first 5 colors
   * for the most important slices.
   *
   * This keeps the chart primarily blue.
   */
  const blueCount =
    Math.min(
      5,
      ranked.length,
    );

  for (
    let rank = 0;
    rank < blueCount;
    rank += 1
  ) {
    const rankedItem =
      ranked[rank];

    /**
     * Spread through the blue palette
     * instead of using neighboring colors.
     *
     * Example:
     *
     * rank 0 → Navy Blue
     * rank 1 → Royal Blue
     * rank 2 → Sky Blue
     * rank 3 → Light Blue
     * rank 4 → Pale Blue
     */
    const paletteIndex =
      Math.floor(
        (rank *
          blueColors.length) /
          blueCount,
      );

    colorMap.set(
      rankedItem.index,
      blueColors[
        Math.min(
          paletteIndex,
          blueColors.length -
            1,
        )
      ],
    );
  }

  /* ----------------------------------------------------------
   * SMALL-SLICE ACCENTS
   * ---------------------------------------------------------- */

  const remainingCount =
    ranked.length -
    blueCount;

  if (
    remainingCount <= 0
  ) {
    return colorMap;
  }

  const accentColors =
    getAccentColors(
      remainingCount,
    );

  for (
    let rank = blueCount;
    rank < ranked.length;
    rank += 1
  ) {
    const accentIndex =
      rank - blueCount;

    const rankedItem =
      ranked[rank];

    const color =
      accentColors[
        accentIndex %
          Math.max(
            accentColors.length,
            1,
          )
      ];

    if (color) {
      colorMap.set(
        rankedItem.index,
        color,
      );
    }
  }

  return colorMap;
};

/* ==========================================================================
 * TOOLTIP
 * ========================================================================== */

const CustomTooltip = ({
  active,
  payload,
}: TooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const item =
    payload[0];

  const name =
    item?.name ||
    item?.payload?.name ||
    '';

  const value =
    Number(
      item?.value ??
        item?.payload?.value ??
        0,
    );

  if (!name) {
    return null;
  }

  return (
    <div
      className="
        min-w-[175px]
        rounded-xl
        border
        border-slate-200
        bg-white
        px-3
        py-2.5
        shadow-lg
      "
    >
      <div
        className="
          flex
          items-center
          justify-between
          gap-5
        "
      >
        <div
          className="
            flex
            min-w-0
            items-center
            gap-2
          "
        >
          <span
            className="
              h-2.5
              w-2.5
              shrink-0
              rounded-full
            "
            style={{
              backgroundColor:
                item?.payload?.color ||
                '#2563EB',
            }}
          />

          <span
            className="
              max-w-[120px]
              truncate
              text-[11px]
              font-medium
              text-[#334155]
            "
            title={name}
          >
            {name}
          </span>
        </div>

        <span
          className="
            shrink-0
            text-[11px]
            font-semibold
            text-[#2563EB]
          "
        >
          {value.toFixed(2)}%
        </span>
      </div>
    </div>
  );
};

/* ==========================================================================
 * COMPONENT
 * ========================================================================== */

const ProductiveAppMixChart = ({
  data,
}: Props) => {
  /* ------------------------------------------------------------------------
   * ACTIVE SLICE
   * ------------------------------------------------------------------------ */

  const [
    activeIndex,
    setActiveIndex,
  ] = useState<number | null>(
    null,
  );

  /* ------------------------------------------------------------------------
   * COLOR MAP
   * ------------------------------------------------------------------------ */

  const colorMap =
    useMemo(
      () =>
        buildChartColors(
          Array.isArray(data)
            ? data
            : [],
        ),
      [data],
    );

  /* ------------------------------------------------------------------------
   * FORMAT DATA
   * ------------------------------------------------------------------------ */

  const chartData =
    useMemo<ChartDataItem[]>(() => {
      if (
        !Array.isArray(data)
      ) {
        return [];
      }

      return data
        .map(
          (item, index) => {
            const name =
              String(
                item.subCategoryName ||
                  'Unknown',
              ).trim() ||
              'Unknown';

            return {
              name,

              value:
                getSafePercentage(
                  item.contributionPercent,
                ),

              /**
               * Color comes from the
               * centralized chartColors.ts.
               *
               * The largest slices are blue.
               * Smaller slices receive accent
               * colors.
               */
              color:
                colorMap.get(
                  index,
                ) ||
                PRODUCTIVE_APP_COLORS[
                  index %
                    PRODUCTIVE_APP_COLORS.length
                ]?.code ||
                '#2563EB',

              originalIndex:
                index,
            };
          },
        )
        .filter(
          (item) =>
            item.value > 0,
        );
    }, [
      data,
      colorMap,
    ]);

  /* ------------------------------------------------------------------------
   * TOTAL
   * ------------------------------------------------------------------------ */

  const totalPercentage =
    useMemo(() => {
      return chartData.reduce(
        (total, item) =>
          total + item.value,
        0,
      );
    }, [chartData]);

  /* ------------------------------------------------------------------------
   * EMPTY STATE
   * ------------------------------------------------------------------------ */

  if (
    chartData.length === 0
  ) {
    return (
      <div
        className="
          flex
          min-h-[395px]
          w-full
          flex-col
          rounded-[18px]
          border
          border-slate-200
          bg-white
          p-4
        "
      >
        <div
          className="
            mb-2
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[14px]
              font-semibold
              text-[#003E7E]
            "
          >
            Productive App Mix
          </h3>

          <span
            className="
              rounded-full
              bg-blue-50
              px-3
              py-1
              text-[10px]
              text-blue-600
            "
          >
            Share of productive hours
            by application
          </span>
        </div>

        <div
          className="
            flex
            flex-1
            items-center
            justify-center
            text-[12px]
            text-slate-500
          "
        >
          No productive application
          data available
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------ */

  return (
    <div
      className="
        w-full
        min-w-0
        rounded-[18px]
        border
        border-slate-200
        bg-white
        p-4
      "
    >
      {/* ================================================================
       * HEADER
       * ================================================================ */}

      <div
        className="
          mb-2
          flex
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[14px]
            font-semibold
            text-[#003E7E]
          "
        >
          Productive App Mix
        </h3>

        <span
          className="
            rounded-full
            bg-blue-50
            px-3
            py-1
            text-[10px]
            text-blue-600
          "
        >
          Share of productive hours
          by application
        </span>
      </div>

      {/* ================================================================
       * DONUT
       * ================================================================ */}

      <div
        className="
          h-[245px]
          w-full
          min-w-0
        "
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart
            margin={{
              top: 4,
              right: 4,
              bottom: 4,
              left: 4,
            }}
          >
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"

              cx="50%"
              cy="50%"

              innerRadius={56}
              outerRadius={88}

              paddingAngle={1}
              minAngle={2}

              stroke="#FFFFFF"
              strokeWidth={1}

              /**
               * Only meaningful slices
               * receive text.
               */
              label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                value,
              }: any) => {
                const numericValue =
                  Number(value);

                if (
                  !shouldShowLabel(
                    numericValue,
                  )
                ) {
                  return null;
                }

                const RADIAN =
                  Math.PI / 180;

                const radius =
                  innerRadius +
                  (outerRadius -
                    innerRadius) *
                    0.5;

                const x =
                  cx +
                  radius *
                    Math.cos(
                      -midAngle *
                        RADIAN,
                    );

                const y =
                  cy +
                  radius *
                    Math.sin(
                      -midAngle *
                        RADIAN,
                    );

                return (
                  <text
                    x={x}
                    y={y}
                    fill="#FFFFFF"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={9}
                    fontWeight={600}
                    pointerEvents="none"
                  >
                    {numericValue.toFixed(
                      2,
                    )}
                    %
                  </text>
                );
              }}

              labelLine={false}

              /**
               * Hover.
               */
              onMouseEnter={(
                _,
                index,
              ) => {
                setActiveIndex(
                  index,
                );
              }}

              onMouseLeave={() => {
                setActiveIndex(
                  null,
                );
              }}

              /**
               * Keep the chart stable.
               */
              isAnimationActive={
                false
              }
            >
              {chartData.map(
                (
                  item,
                  index,
                ) => (
                  <Cell
                    key={`${item.name}-${index}`}
                    fill={item.color}
                    stroke="#FFFFFF"
                    strokeWidth={1}
                    fillOpacity={
                      activeIndex ===
                        null ||
                      activeIndex ===
                        index
                        ? 1
                        : 0.25
                    }
                  />
                ),
              )}
            </Pie>

            {/* ==========================================================
             * TOOLTIP
             * ========================================================== */}

            <Tooltip
              content={
                <CustomTooltip />
              }
              wrapperStyle={{
                outline: 'none',
                zIndex: 1000,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* ================================================================
       * LEGEND
       * ================================================================ */}

      <div
        className="
          mt-3
          flex
          flex-wrap
          justify-center
          gap-x-4
          gap-y-2
        "
      >
        {chartData.map(
          (
            item,
            index,
          ) => {
            const isActive =
              activeIndex === null ||
              activeIndex === index;

            return (
              <div
                key={`${item.name}-legend-${index}`}
                className="
                  flex
                  min-w-0
                  cursor-pointer
                  items-center
                  gap-1.5
                  text-[11px]
                  transition-all
                  duration-200
                "
                style={{
                  opacity:
                    isActive
                      ? 1
                      : 0.35,
                }}
                onMouseEnter={() =>
                  setActiveIndex(
                    index,
                  )
                }
                onMouseLeave={() =>
                  setActiveIndex(
                    null,
                  )
                }
              >
                {/* COLOR DOT */}

                <span
                  className="
                    h-3
                    w-3
                    shrink-0
                    rounded-full
                  "
                  style={{
                    backgroundColor:
                      item.color,
                  }}
                />

                {/* NAME */}

                <span
                  className={`
                    whitespace-nowrap
                    ${
                      activeIndex ===
                      index
                        ? 'font-semibold text-[#003E7E]'
                        : 'text-[#334155]'
                    }
                  `}
                  title={item.name}
                >
                  {item.name}
                </span>
              </div>
            );
          },
        )}
      </div>

      {/* ================================================================
       * TOTAL
       * ================================================================ */}

      {Math.abs(
        totalPercentage - 100,
      ) > 0.1 && (
        <div
          className="
            mt-2
            text-center
            text-[9px]
            text-slate-400
          "
        >
          Total:{' '}
          {totalPercentage.toFixed(
            2,
          )}
          %
        </div>
      )}
    </div>
  );
};

export default ProductiveAppMixChart;